import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Edit, Trash2, X } from 'lucide-react';
import fingerprintApi, { FingerprintDevice } from '../services/fingerprintApi';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';

interface DeviceForm {
  deviceId: number;
  name: string;
  tenantId?: number;
  status: 'active' | 'inactive';
}

const FingerprintDevices: React.FC = () => {
  const { user } = useAuth();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingDevice, setEditingDevice] = useState<FingerprintDevice | null>(null);
  const [deletingDevice, setDeletingDevice] = useState<FingerprintDevice | null>(null);
  const queryClient = useQueryClient();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
    setValue
  } = useForm<DeviceForm>();

  // Fetch devices
  const { data: devicesResponse, isLoading } = useQuery({
    queryKey: ['fingerprint-devices'],
    queryFn: () => fingerprintApi.getFingerprintDevices(),
    enabled: user?.role === 'SUPERADMIN'
  });

  const devices: FingerprintDevice[] = devicesResponse?.devices || [];

  // Create device mutation
  const createMutation = useMutation({
    mutationFn: (data: DeviceForm) => fingerprintApi.createFingerprintDevice({
      deviceId: data.deviceId,
      name: data.name,
      tenantId: data.tenantId,
      status: data.status
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['fingerprint-devices'] });
      queryClient.invalidateQueries({ queryKey: ['fingerprint-devices-my'] });
      toast.success('Device created successfully');
      setIsModalOpen(false);
      reset();
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to create device');
    },
  });

  // Update device mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<DeviceForm> }) =>
      fingerprintApi.updateFingerprintDevice(id, {
        name: data.name,
        tenantId: data.tenantId,
        status: data.status,
        deviceId: data.deviceId
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['fingerprint-devices'] });
      queryClient.invalidateQueries({ queryKey: ['fingerprint-devices-my'] });
      toast.success('Device updated successfully');
      setIsModalOpen(false);
      setEditingDevice(null);
      reset();
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to update device');
    },
  });

  // Delete device mutation
  const deleteMutation = useMutation({
    mutationFn: (id: number) => fingerprintApi.deleteFingerprintDevice(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['fingerprint-devices'] });
      queryClient.invalidateQueries({ queryKey: ['fingerprint-devices-my'] });
      toast.success('Device deleted successfully');
      setDeletingDevice(null);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to delete device');
    },
  });

  const onSubmit = (data: DeviceForm) => {
    if (editingDevice) {
      updateMutation.mutate({ id: editingDevice.id, data });
    } else {
      createMutation.mutate(data);
    }
  };

  const handleEdit = (device: FingerprintDevice) => {
    setEditingDevice(device);
    setValue('deviceId', device.deviceId);
    setValue('name', device.name);
    setValue('tenantId', device.tenantId || undefined);
    setValue('status', device.status);
    setIsModalOpen(true);
  };

  const handleDelete = (device: FingerprintDevice) => {
    setDeletingDevice(device);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingDevice(null);
    reset();
  };

  if (user?.role !== 'SUPERADMIN') {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">Access denied. SuperAdmin only.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Fingerprint Devices</h1>
        <button
          onClick={() => {
            reset();
            setEditingDevice(null);
            setIsModalOpen(true);
          }}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
        >
          <Plus className="w-5 h-5" />
          Add Device
        </button>
      </div>

      {isLoading ? (
        <div className="text-center py-8">Loading...</div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Device ID
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Tenant ID
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {devices.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-6 py-4 text-center text-gray-500">
                    No devices found
                  </td>
                </tr>
              ) : (
                devices.map((device) => (
                  <tr key={device.id}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {device.deviceId}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {device.name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {device.tenantId || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-medium ${
                          device.status === 'active'
                            ? 'bg-green-100 text-green-800'
                            : 'bg-red-100 text-red-800'
                        }`}
                      >
                        {device.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleEdit(device)}
                          className="text-blue-600 hover:text-blue-900"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(device)}
                          className="text-red-600 hover:text-red-900"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Create/Edit Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-md shadow-lg rounded-md bg-white">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                {editingDevice ? 'Edit Device' : 'Add Device'}
              </h3>
              <button
                onClick={handleCloseModal}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Device ID (Numeric)
                </label>
                <input
                  type="number"
                  {...register('deviceId', {
                    required: 'Device ID is required',
                    valueAsNumber: true
                  })}
                  disabled={!!editingDevice}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
                />
                {errors.deviceId && (
                  <p className="mt-1 text-sm text-red-600">{errors.deviceId.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Device Name
                </label>
                <input
                  type="text"
                  {...register('name', { required: 'Device name is required' })}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                {errors.name && (
                  <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Tenant ID (Optional)
                </label>
                <input
                  type="number"
                  {...register('tenantId', { valueAsNumber: true })}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Leave empty for global device. All admins in the tenant will have access.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Status
                </label>
                <select
                  {...register('status', { required: 'Status is required' })}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
                {errors.status && (
                  <p className="mt-1 text-sm text-red-600">{errors.status.message}</p>
                )}
              </div>

              <div className="flex gap-2 pt-4">
                <button
                  type="submit"
                  disabled={createMutation.isPending || updateMutation.isPending}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                >
                  {editingDevice ? 'Update' : 'Create'}
                </button>
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="flex-1 px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deletingDevice && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-md shadow-lg rounded-md bg-white">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Delete Device</h3>
            <p className="text-sm text-gray-600 mb-4">
              Are you sure you want to delete device "{deletingDevice.name}" (ID: {deletingDevice.deviceId})?
              This action cannot be undone.
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => deleteMutation.mutate(deletingDevice.id)}
                disabled={deleteMutation.isPending}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50"
              >
                Delete
              </button>
              <button
                onClick={() => setDeletingDevice(null)}
                className="flex-1 px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FingerprintDevices;

